'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { Rating } from '@/features/reviews/rating'
import { formatRelativeTime } from '@/utils/functions/formatRelativeTime'
import { createImageUrl } from '@/utils/image/create-image-url'
import { cn } from '@/utils/styles/cn'

export interface IRestaurantDetailReview {
    name: string
    rating: number
    body?: string
    dateTime?: string
    userImageUrl?: string
    photos?: { uuid: string; url: string }[]
}

interface IRestaurantDetailReviewCardProps {
    review: IRestaurantDetailReview
    className?: string
}

/** Above roughly this many characters a review gets clamped behind "read more" */
const CLAMP_THRESHOLD_CHARS = 280

/**
 * Borderless review block: header, stars, clamped text, photo thumbnails.
 * Long texts expand inline so grid rows stay roughly even.
 */
export const RestaurantDetailReviewCard = ({
    review,
    className,
}: IRestaurantDetailReviewCardProps) => {
    const locale = useLocale()
    const t = useTranslations()

    const [isTextExpanded, setIsTextExpanded] = useState(false)

    const readableDate = useMemo(() => {
        if (!review.dateTime) return

        return formatRelativeTime(new Date(review.dateTime), locale)
    }, [review.dateTime, locale])

    const customerName = review.name?.trim()

    const needsClamp = Boolean(review.body && review.body.length > CLAMP_THRESHOLD_CHARS)

    return (
        <div className={cn('flex flex-col', className)}>
            {customerName && (
                <div className="flex items-center gap-x-3">
                    {review.userImageUrl ? (
                        <Image
                            unoptimized
                            src={createImageUrl(review.userImageUrl, { width: 36, height: 36 })}
                            alt=""
                            width={36}
                            height={36}
                            className="size-9 shrink-0 rounded-full object-cover"
                        />
                    ) : (
                        <div
                            className="flex size-9 shrink-0 items-center justify-center rounded-full
                                bg-bg-neutral-surface-medium text-label-md text-fg-secondary uppercase"
                        >
                            {customerName[0]}
                        </div>
                    )}

                    <div className="flex flex-col">
                        <span className="text-label-lg">{customerName}</span>

                        {readableDate && (
                            <span suppressHydrationWarning className="text-p-xs text-fg-tertiary">
                                {readableDate}
                            </span>
                        )}
                    </div>
                </div>
            )}

            <Rating
                withReadableRating={false}
                rating={review.rating}
                starsStyleType="maximal"
                starClassName="w-3.5 h-3.5"
                starsClassName="gap-[2px]"
                ratingCountStyleType="only-count"
                containerClassName="mt-3"
            />

            {review.body && (
                <p
                    className={cn(
                        'mt-2 text-p-md text-fg-secondary',
                        needsClamp && !isTextExpanded && 'line-clamp-4'
                    )}
                >
                    {review.body}
                </p>
            )}

            {needsClamp && (
                <button
                    type="button"
                    onClick={() => setIsTextExpanded((prev) => !prev)}
                    className="mt-1 w-fit cursor-pointer text-p-sm font-semibold text-fg-primary
                        underline underline-offset-4 webkit-tap-transparent"
                >
                    {isTextExpanded
                        ? t('restaurants.detail.reviews.readLess')
                        : t('restaurants.detail.reviews.readMore')}
                </button>
            )}

            {Boolean(review.photos?.length) && review.photos && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {review.photos.map((photo) => (
                        <div
                            key={photo.uuid}
                            className="size-16 shrink-0 overflow-hidden rounded-s
                                bg-bg-neutral-surface"
                        >
                            <Image
                                unoptimized
                                src={createImageUrl(photo.url, { width: 64, height: 64 })}
                                alt=""
                                width={64}
                                height={64}
                                className="size-full object-cover"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
